import {
    timeRangeZodSchema,
    type TimeRange,
} from "@@/shared/date-time/time-range";
import { zeroPrice, type Price } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import type { ProductId} from "@@/shared/products/product-id";
import { productIdZodSchema } from "@@/shared/products/product-id";
import type {
    Translatable} from "@@/shared/translations/translation";
import {
    translatableZodSchema,
} from "@@/shared/translations/translation";
import { repeat } from "@@/utilities/repeat";
import { z } from "zod";
import { orderItemZodSchema, type OrderItem } from "../order-items/order-item";
import {
    orderItemIdZodSchema,
    type OrderItemId,
} from "../order-items/order-item-id";
import {
    orderChangeItemIdFactory,
    orderChangeItemIdZodSchema,
} from "./order-change-item-id";

const orderChangeItem_add = z.object({
    _id: orderChangeItemIdZodSchema,
    _type: z.literal("ORDER_ITEM_ADD"),
    itemId: orderItemIdZodSchema,
    orderItem: orderItemZodSchema,
});
type OrderChangeItem_Add = z.infer<typeof orderChangeItem_add>;

const orderChangeItem_changeProduct = z.object({
    _id: orderChangeItemIdZodSchema,
    _type: z.literal("ORDER_ITEM_CHANGE_PRODUCT"),
    itemId: orderItemIdZodSchema,
    from: z.object({
        id: productIdZodSchema,
        title: translatableZodSchema,
    }),

    to: z.object({
        id: productIdZodSchema,
        title: translatableZodSchema,
    }),
});
type OrderChangeItem_ChangeProduct = z.infer<
    typeof orderChangeItem_changeProduct
>;

const orderChangeItem_remove = z.object({
    _id: orderChangeItemIdZodSchema,
    _type: z.literal("ORDER_ITEM_REMOVE"),
    itemId: orderItemIdZodSchema,
    orderItem: orderItemZodSchema,
});
type OrderChangeItem_Remove = z.infer<typeof orderChangeItem_remove>;

const orderChangeItem_bookableItemChangeTime = z.object({
    _id: orderChangeItemIdZodSchema,
    _type: z.literal("ORDER_ITEM_BOOKABLE_CHANGE_TIME"),
    itemId: orderItemIdZodSchema,
    from: timeRangeZodSchema,
    to: timeRangeZodSchema,
});
type OrderChangeItem_BookableItemChangeTime = z.infer<
    typeof orderChangeItem_bookableItemChangeTime
>;

const orderChangeItemZodUnion = z.discriminatedUnion("_type", [
    orderChangeItem_add,
    orderChangeItem_remove,
    orderChangeItem_bookableItemChangeTime,
    orderChangeItem_changeProduct,
]);

type OrderChangeItem = z.infer<typeof orderChangeItemZodUnion>;
const orderChangeItemZodSchema: z.ZodSchema<OrderChangeItem> =
    orderChangeItemZodUnion;

const orderAddItemFactory = (newItem: OrderItem): OrderChangeItem_Add => {
    return {
        _id: orderChangeItemIdFactory(),
        _type: "ORDER_ITEM_ADD",
        orderItem: newItem,
        itemId: newItem._id,
    };
};

const orderRemoveItemFactory = (item: OrderItem): OrderChangeItem_Remove => ({
    _id: orderChangeItemIdFactory(),
    _type: "ORDER_ITEM_REMOVE",
    orderItem: item,
    itemId: item._id,
});

const orderItemChangeTimeFactory = (
    itemId: OrderItemId,
    from: TimeRange,
    to: TimeRange,
): OrderChangeItem_BookableItemChangeTime => {
    return {
        _id: orderChangeItemIdFactory(),
        _type: "ORDER_ITEM_BOOKABLE_CHANGE_TIME",
        itemId,
        from,
        to,
    };
};

const orderItemChangeProductFactory = (
    itemId: OrderItemId,
    from: { id: ProductId; title: Translatable },
    to: { id: ProductId; title: Translatable },
): OrderChangeItem_ChangeProduct => {
    return {
        _id: orderChangeItemIdFactory(),
        _type: "ORDER_ITEM_CHANGE_PRODUCT",
        itemId,
        from,
        to,
    };
};

const isOrderChangeItem = (input: unknown): input is OrderChangeItem => {
    if (
        input &&
        typeof input === "object" &&
        "_type" in input &&
        input._type === "ORDER_CHANGE_ITEM"
    ) {
        return true;
    }
    return false;
};

const calculateTotalPriceOfOrderChangeItems = (
    orderChangeItems: OrderChangeItem[],
): Price => {
    return sumPrices(
        orderChangeItems.flatMap(changeItem =>
            changeItem._type === "ORDER_ITEM_BOOKABLE_CHANGE_TIME" ||
            changeItem._type === "ORDER_ITEM_CHANGE_PRODUCT"
                ? zeroPrice
                : repeat(
                      changeItem.orderItem.pricePerItem,
                      changeItem.orderItem.quantity.value,
                  ),
        ),
    );
};

export {
    calculateTotalPriceOfOrderChangeItems,
    isOrderChangeItem,
    orderAddItemFactory,
    orderChangeItemZodSchema,
    orderChangeItemZodUnion,
    orderItemChangeProductFactory,
    orderItemChangeTimeFactory,
    orderRemoveItemFactory,
};

export type {
    OrderChangeItem_BookableItemChangeTime as OrderBookableItemChangeTime,
    OrderChangeItem,
    OrderChangeItem_Add as OrderItemAdd,
    OrderChangeItem_ChangeProduct as OrderItemChangeProduct,
    OrderChangeItem_Remove as OrderItemRemove,
};
